import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";
import { isAbortError } from "../../utils/common";

interface SKU {
  SkuId: string;
  ProductId: string;
  ProductSkuNum: number;
  Name: string;
  Description: string;
  CostMacroCategoryCd: string;
  CostMetricCd: string;
  CostMetricCost: number;
  CurrencyCd: string;
  CostIntervalCd: string;
  CostIntervalMin: number;
  CostIntervalMax: number;
  IsAutoProvisioned: boolean;
  IsReturnable: boolean;
  StatusCd: string;
  CreatedUser: string;
  UpdatedUser: string;
  CreatedDtm: string;
  UpdatedDtm: string;
  OrderQuantity: number;
  OrderInterval: number;
}

interface Order {
  OrderParentId: number;
  Description: string;
  OpportunityId: string;
  EngagementId: string;
  ServiceCodes: null;
  StatusCd: string;
  CreatedDtm: string;
  UpdatedDtm: string;
  CreatedUser: string;
  UpdatedUser: string;
  Requestor: null;
  Details: [
    {
      ProductName: string;
      AssetAdvice: string;
      SkuDetails: SKU[];
    },
  ];
}

interface ProductListState {
  isLoading: boolean;
  data: Order[] | null;
  error: string | null;
  approveStatus: "idle" | "loading" | "success" | "error";
  approveError: string | null;
  rejectStatus: "idle" | "loading" | "success" | "error";
  rejectError: string | null;
}

export interface ResponseError {
  message: string;
}

export interface RequestObject {
  Action: string;
  Status?: string | string[];
  Page?: number;
  Record?: number;
  SearchText?: string;
  StartDate?: string | null;
  EndDate?: string | null;
  ProvisioningStatus?: string[];
  OrderStatus?: string;
  OrderSkuId?: number | string;
  ReturnReason?: string;
  Transactions?: ReturnTransactionPayload[];
  Type?: string[];
  RequestedDate?: string;
  OrderId?: string;
  SkuId?: string;
  Role?: string;
  Source?: string;
  ReturnId?: string;
  signal?: AbortSignal;
}

export interface RecoveriesRequestObject {
  Page?: number;
  Record?: number;
  BillingType?: string;
  StartDate?: string | null;
  EndDate?: string | null;
  TransactionDateFilter?: string;
  search?: string;
  SkuId?: string;
  OrderId?: number;
}

export interface ReturnTransactionPayload {
  OrderSkuId: number;
  Quantity: number;
  TotalCost: number;
  TransactionId: number;
}

const initialState: ProductListState = {
  isLoading: true,
  data: null,
  error: null,
  approveStatus: "idle",
  approveError: null,
  rejectStatus: "idle",
  rejectError: null,
};

const isGatewayInvalidInputError = (error: any) =>
  error?.status === 403 ||
  error?.response?.status === 403 ||
  error?.request?.status === 403 ||
  error?.code === "ERR_NETWORK" ||
  error?.message === "Network Error";

export interface CancelRequestObj {
  Action: string;
  OrderSkuId: number;
}

export const fetchOrderPipelineListData = createAsyncThunk<
  any,
  RequestObject,
  { rejectValue: ResponseError }
>(
  "OrderPipelineSlice/fetchOrderPipelineListData",
  async (requestObject, thunkApi) => {
    try {
      const { signal } = requestObject;
      const response = await httpClient.post("/order", requestObject, {
        signal: signal,
      });

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to fetch order list",
          });
        }
      }
      return response.data;
    } catch (error: any) {
      if (isAbortError(error) || error?.code === "ERR_CANCELED") {
        // Handle abort
        return;
      }
      if (
        requestObject.Action === "submitordertransaction" &&
        isGatewayInvalidInputError(error)
      ) {
        return thunkApi.rejectWithValue({
          message:
            "Invalid input detected. Please review the input text and try again!",
        });
      }
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: "Failed to fetch order list",
      });
    }
  },
);

export const fetchRecoveriesListData = createAsyncThunk<
  any,
  { requestObject: RecoveriesRequestObject },
  { rejectValue: ResponseError }
>(
  "OrderPipelineSlice/fetchRecoveriesListData",
  async ({ requestObject }, thunkApi) => {
    try {
      // Conditionally include the search parameter only if searchText is not empty

      const reqObj: any = { ...requestObject };

      ["StartDate", "EndDate"].forEach((key) => {
        if (reqObj[key] == null) delete reqObj[key];
      });
      ["BillingType", "TransactionDateFilter"].forEach((key) => {
        if (reqObj[key] === "") delete reqObj[key];
      });

      const response = await httpClient.post("/db/recoveries", reqObj);

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to fetch recoveries list",
          });
        }
      }
      return response.data;
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: "Failed to fetch recoveries list",
      });
    }
  },
);

// Approve SKU thunk
export const approveOrderSku = createAsyncThunk<
  any,
  { SkuId: string; OrderSkuId: number; Role: string; OrderParentId?: number },
  { rejectValue: ResponseError }
>("OrderPipelineSlice/approveOrderSku", async (payload, thunkApi) => {
  try {
    const response = await httpClient.post("product/sku", {
      Action: "approve",
      ...payload,
    });
    if (response.status !== 200 || response.data.Status !== "success") {
      return thunkApi.rejectWithValue({
        message: response.data.Message || "Failed to approve SKU",
      });
    }
    return response.data;
  } catch (error: any) {
    return thunkApi.rejectWithValue({
      message: error?.response?.data?.Message || "Failed to approve SKU",
    });
  }
});

// Reject SKU thunk
export const rejectOrderSku = createAsyncThunk<
  any,
  {
    SkuId: string;
    text: string;
    OrderSkuId: number;
    Role: string;
    OrderParentId?: number;
  },
  { rejectValue: ResponseError }
>("OrderPipelineSlice/rejectOrderSku", async (payload, thunkApi) => {
  try {
    const response = await httpClient.post("product/sku", {
      Action: "rejectordersku",
      ...payload,
    });
    if (response.status !== 200 || response.data.Status !== "success") {
      return thunkApi.rejectWithValue({
        message: response.data.Message || "Failed to reject SKU",
      });
    }
    return response.data;
  } catch (error: any) {
    if (isGatewayInvalidInputError(error)) {
      return thunkApi.rejectWithValue({
        message:
          "Invalid input detected. Please review the input text and try again!",
      });
    } else {
      return thunkApi.rejectWithValue({
        message: error?.response?.data?.Message || "Failed to reject SKU",
      });
    }
  }
});
export const cancelOrder = createAsyncThunk<
  any,
  CancelRequestObj,
  { rejectValue: ResponseError }
>("OrderPipelineSlice/cancelOrder", async (requestObject, thunkApi) => {
  try {
    const response = await httpClient.post("/order", requestObject);

    if (response.status != 200 || response.data.Status !== "success") {
      if (response.data.Status == "error") {
        return thunkApi.rejectWithValue({
          message: response.data.Message,
        });
      } else {
        return thunkApi.rejectWithValue({
          message: "Failed to cancel order",
        });
      }
    }
    return response.data;
  } catch (error: any) {
    if (isAbortError(error) || error?.code === "ERR_CANCELED") {
      // Handle abort
      return;
    }
    console.log("🚀 ~ error:", error);
    return thunkApi.rejectWithValue({
      message: "Failed to cancel order",
    });
  }
});

export const fetchOrderExportData = createAsyncThunk<
  any,
  { Action: string; SearchText: string }
>("OrderPipelineSlice/fetchOrderExportData", async (payload, thunkApi) => {
  try {
    const response = await httpClient.post("/order", payload);
    if (response.status !== 200) {
      return thunkApi.rejectWithValue({
        message:
          response?.data?.Message || "Failed to get consumption export data",
      });
    }
    return response.data;
  } catch (error: any) {
    if (isAbortError(error) || error?.code === "ERR_CANCELED") {
      // Handle abort
      return;
    }
    return thunkApi.rejectWithValue({
      message: error?.message || "Failed to get consumption export data",
    });
  }
});

const OrderPipelineSlice = createSlice({
  name: "OrderPipelineSlice",
  initialState,
  reducers: {
    resetOrderPipelineListState: (state) => {
      state.isLoading = false;
      state.data = null;
      state.error = null;
      state.approveStatus = "idle";
      state.approveError = null;
      state.rejectStatus = "idle";
      state.rejectError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrderPipelineListData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        fetchOrderPipelineListData.fulfilled,
        (state, action: PayloadAction<any>) => {
          if (
            action?.payload?.Status == "success" &&
            action?.payload !== undefined
          ) {
            state.isLoading = false;
            state.data = action.payload.Data;
            state.error = null;
          } else if (action?.payload?.Status == "error") {
            state.isLoading = false;
            state.data = null;
            state.error = action.payload.message;
          }
        },
      )
      .addCase(
        fetchOrderPipelineListData.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.data = null;
          state.error = action.payload.message || "Failed to fetch order list";
        },
      )

      // Add recoveries cases
      .addCase(fetchRecoveriesListData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(
        fetchRecoveriesListData.fulfilled,
        (state, action: PayloadAction<any>) => {
          if (action.payload.Status == "success") {
            state.isLoading = false;
            state.data = action.payload.Data;
            state.error = null;
          } else if (action.payload.Status == "error") {
            state.isLoading = false;
            state.data = null;
            state.error = action.payload.message;
          }
        },
      )
      .addCase(
        fetchRecoveriesListData.rejected,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.data = null;
          state.error =
            action.payload.message || "Failed to fetch recoveries list";
        },
      )

      // Approve SKU cases
      .addCase(approveOrderSku.pending, (state) => {
        state.approveStatus = "loading";
        state.approveError = null;
      })
      .addCase(approveOrderSku.fulfilled, (state) => {
        state.approveStatus = "success";
        state.approveError = null;
      })
      .addCase(
        approveOrderSku.rejected,
        (state, action: PayloadAction<any>) => {
          state.approveStatus = "error";
          state.approveError =
            action.payload?.message || "Failed to approve SKU";
        },
      )
      // Reject SKU cases
      .addCase(rejectOrderSku.pending, (state) => {
        state.rejectStatus = "loading";
        state.rejectError = null;
      })
      .addCase(rejectOrderSku.fulfilled, (state) => {
        state.rejectStatus = "success";
        state.rejectError = null;
      })
      .addCase(rejectOrderSku.rejected, (state, action: PayloadAction<any>) => {
        state.rejectStatus = "error";
        state.rejectError = action.payload?.message || "Failed to reject SKU";
      });
  },
});

export const { resetOrderPipelineListState } = OrderPipelineSlice.actions;

export default OrderPipelineSlice.reducer;
